import React, { useEffect, useState } from "react";
import { dms } from "./api";

const inr = (n) => "₹ " + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const box = { background: "var(--card, #fff)", border: "1px solid rgba(128,128,128,0.25)", borderRadius: 14, padding: 16 };

function Section({ title, hint, children }) {
  return (
    <div style={box}>
      <div style={{ fontWeight: 600 }}>{title}</div>
      {hint ? <div style={{ fontSize: 12, opacity: 0.6, marginTop: 2 }}>{hint}</div> : null}
      <div style={{ marginTop: 12 }}>{children}</div>
    </div>
  );
}
function Empty({ text }) { return <div style={{ fontSize: 13, opacity: 0.6 }}>{text}</div>; }
function Stat({ label, value, sub }) {
  return (
    <div style={{ border: "1px solid rgba(128,128,128,0.2)", borderRadius: 10, padding: "10px 12px" }}>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.8, opacity: 0.6 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, marginTop: 2 }}>{value}</div>
      {sub ? <div style={{ fontSize: 11, opacity: 0.6 }}>{sub}</div> : null}
    </div>
  );
}
function Bar({ pct, color }) {
  return (
    <div style={{ height: 8, borderRadius: 999, background: "rgba(128,128,128,0.18)", overflow: "hidden" }}>
      <div style={{ width: Math.min(100, Math.max(0, pct || 0)) + "%", height: "100%", background: color || "#b8860b" }} />
    </div>
  );
}
const pctColor = (p) => (p === null || p === undefined ? "#64748b" : p >= 100 ? "#b91c1c" : p >= 75 ? "#c2410c" : p >= 50 ? "#b45309" : "#15803d");

function Rows({ rows, left, right }) {
  return (
    <div>
      {rows.map((r, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 13, padding: "6px 0", borderTop: i ? "1px solid rgba(128,128,128,0.15)" : "none" }}>
          <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{left(r)}</span>
          <b style={{ whiteSpace: "nowrap" }}>{right(r)}</b>
        </div>
      ))}
    </div>
  );
}
function CreditList({ title, rows }) {
  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>{title}</div>
      {rows.length === 0 ? <Empty text="Nothing outstanding" /> : rows.map((r, i) => (
        <div key={i} style={{ marginBottom: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 13, flexWrap: "wrap" }}>
            <span>{r.name}</span>
            <span><b>{inr(r.used)}</b>{r.limit > 0 ? " of " + inr(r.limit) + " (" + r.pct + "%)" : " (no credit limit set)"}</span>
          </div>
          <Bar pct={r.pct === null ? 0 : r.pct} color={pctColor(r.pct)} />
        </div>
      ))}
    </div>
  );
}

export default function OwnerMoreV2({ period, distId }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let alive = true;
    setErr(false);
    dms.ownerMoreV2({ period: period, distributor_id: distId })
      .then((x) => { if (alive) setD(x); })
      .catch(() => { if (alive) setErr(true); });
    return () => { alive = false; };
  }, [period, distId]);

  if (err) return <div style={{ ...box, color: "#b91c1c" }}>Could not load more insights. Please refresh the page.</div>;
  if (!d) return <div style={{ ...box, opacity: 0.7 }}>Loading more insights...</div>;

  const wmax = Math.max(1, ...d.weekday.map((w) => w.amount));
  const anyDay = d.weekday.some((w) => w.amount > 0);
  const wbest = d.weekday.reduce((b, w) => (w.amount > b.amount ? w : b), d.weekday[0]);
  const cp = d.coupons;
  const sp = d.speed;
  const cr = d.credit;

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))", gap: 16 }}>
        <Section title="Sales by Category" hint="Retailer sales in the selected period. Share = part of total sales.">
          {d.categories.length === 0 ? <Empty text="No sales in this period" /> : d.categories.map((c) => (
            <div key={c.name} style={{ marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 13, flexWrap: "wrap" }}>
                <span>{c.name}</span><span><b>{inr(c.amount)}</b> | {c.litres} L | {c.share}%</span>
              </div>
              <Bar pct={c.share} />
            </div>
          ))}
        </Section>

        <Section title="Best Selling Weekday" hint="Retailer bills of the last 90 days, grouped by day of the week.">
          {!anyDay ? <Empty text="No bills in the last 90 days" /> : (
            <div>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 120 }}>
                {d.weekday.map((w) => (
                  <div key={w.day} style={{ flex: 1, textAlign: "center" }} title={inr(w.amount) + " in " + w.bills + " bills"}>
                    <div style={{ height: Math.max(2, (w.amount / wmax) * 90), background: w.day === wbest.day ? "#b8860b" : "rgba(37,99,235,0.55)", borderRadius: 4 }} />
                    <div style={{ fontSize: 11, marginTop: 4 }}>{w.day}</div>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 13, marginTop: 10 }}>Best day: <b>{wbest.day}</b> ({inr(wbest.amount)} in {wbest.bills} bills). Gold bar = best day.</div>
            </div>
          )}
        </Section>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))", gap: 16 }}>
        <Section title="Pending Demand" hint="Retailers asked for these items, but they were not supplied yet.">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 8, marginBottom: 12 }}>
            <Stat label="Boxes pending" value={d.pending.boxes} />
            <Stat label="Retailers waiting" value={d.pending.retailers} />
          </div>
          {d.pending.rows.length ? <Rows rows={d.pending.rows} left={(r) => r.name} right={(r) => r.boxes + " boxes"} /> : <Empty text="Nothing pending" />}
        </Section>

        <Section title="Order Speed" hint="Average time for primary orders (all orders so far).">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: 8 }}>
            <Stat label="Order to ready" value={sp.days_to_ready === null ? "-" : sp.days_to_ready + " days"} sub="Time to prepare the order" />
            <Stat label="Ready to received" value={sp.days_to_received === null ? "-" : sp.days_to_received + " days"} sub="Time to reach distributor" />
            <Stat label="Backorders" value={sp.backorders} sub={"out of " + sp.orders + " orders"} />
          </div>
        </Section>
      </div>

      <Section title="Credit Usage" hint={"How much of the credit limit is used. Red = limit crossed. Retailers over limit: " + cr.retailers_over_limit}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))", gap: 24 }}>
          <CreditList title="Distributors" rows={cr.distributors} />
          <CreditList title="Retailers (top 6)" rows={cr.retailers} />
        </div>
      </Section>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))", gap: 16 }}>
        <Section title="Coupons" hint="Coupon claims by retailers.">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))", gap: 8 }}>
            <Stat label="Claimed (all time)" value={cp.claimed_total} sub={inr(cp.claimed_value)} />
            <Stat label="Claimed (period)" value={cp.claimed_period} sub={inr(cp.claimed_period_value)} />
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, margin: "12px 0" }}>
            {Object.keys(cp.status || {}).map((k) => (
              <div key={k} style={{ border: "1px solid rgba(128,128,128,0.3)", borderRadius: 999, padding: "3px 12px", fontSize: 12 }}>{k}: <b>{cp.status[k]}</b></div>
            ))}
          </div>
          {cp.by_distributor.length ? <Rows rows={cp.by_distributor} left={(r) => r.name} right={(r) => r.claims + " claims"} /> : <Empty text="No claims yet" />}
        </Section>

        <Section title="Expenses" hint="Approved and pending expenses in the selected period.">
          <Stat label="Total expenses" value={inr(d.expenses.total)} />
          <div style={{ marginTop: 12 }}>
            {d.expenses.rows.length ? <Rows rows={d.expenses.rows} left={(r) => r.name} right={(r) => inr(r.amount)} /> : <Empty text="No expenses in this period" />}
          </div>
        </Section>

        <Section title="Support Tickets">
          <Stat label="Open tickets" value={d.tickets.open} />
          <div style={{ marginTop: 12 }}>
            {d.tickets.latest.length ? <Rows rows={d.tickets.latest} left={(r) => (r.no || "") + " " + (r.subject || "")} right={(r) => r.status} /> : <Empty text="No open tickets" />}
          </div>
        </Section>
      </div>
    </div>
  );
}
// END_MORE_V2
